import React, { useState, useEffect, useRef } from "react";
import { FaLock, FaShieldAlt, FaVideo, FaGlobe, FaCheckCircle, FaArrowRight, FaStar, FaTrophy, FaUsers, FaClock } from "react-icons/fa";
import "../styles/WhyChooseUs.css";

const WhyChooseUs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const features = [
    {
      id: 1,
      title: "100% Confidential",
      description: "Your privacy is our priority. All cases handled with utmost discretion and strict non-disclosure agreements.",
      icon: <FaLock />,
      stat: "10,000+",
      statLabel: "Confidential Cases",
      color: "#3b82f6",
      gradient: "linear-gradient(135deg, #3b82f6, #2563eb)",
      delay: 0.1,
    },
    {
      id: 2,
      title: "Licensed Professionals",
      description: "Government licensed detectives with years of field experience and proven track record.",
      icon: <FaShieldAlt />,
      stat: "50+",
      statLabel: "Expert Detectives",
      color: "#06d881",
      gradient: "linear-gradient(135deg, #06d881, #059669)",
      delay: 0.2,
    },
    {
      id: 3,
      title: "Advanced Technology",
      description: "Latest surveillance equipment, forensic tools, and cutting-edge investigation methods.",
      icon: <FaVideo />,
      stat: "24/7",
      statLabel: "Tech Support",
      color: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b, #d97706)",
      delay: 0.3,
    },
    {
      id: 4,
      title: "Pan India Network",
      description: "Operations across all major cities in India with a wide network of professionals.",
      icon: <FaGlobe />,
      stat: "25+",
      statLabel: "Cities Covered",
      color: "#ef4444",
      gradient: "linear-gradient(135deg, #ef4444, #dc2626)",
      delay: 0.4,
    },
  ];

  const stats = [
    { icon: <FaTrophy />, value: "5000+", label: "Cases Solved", color: "#3b82f6" },
    { icon: <FaStar />, value: "98%", label: "Success Rate", color: "#f59e0b" },
    { icon: <FaUsers />, value: "10k+", label: "Happy Clients", color: "#06d881" },
    { icon: <FaClock />, value: "15+", label: "Years Experience", color: "#ef4444" },
  ];

  return (
    <section className="why-choose-premium" ref={sectionRef}>
      {/* Animated Background */}
      <div className="why-choose-bg">
        <div className="bg-wave"></div>
        <div className="bg-dots"></div>
        <div className="bg-grid"></div>
      </div>

      <div className="why-choose-premium-container">
        {/* Header Section */}
        <div className="why-choose-header">
          <div className="header-icon-wrapper">
            <div className="header-icon-pulse"></div>
            <FaStar className="header-star" />
          </div>
          <span className="premium-badge">
            <span className="badge-text">Why Choose Us</span>
          </span>
          <h2 className="premium-title">
            What Makes Us <span className="title-gradient"> Different?</span>
            
          </h2>
          <div className="title-ornament">
            <svg width="120" height="20" viewBox="0 0 120 20">
              <path d="M0,10 L20,10 M30,10 L50,10 M60,10 L80,10 M90,10 L110,10" stroke="#3b82f6" strokeWidth="2"/>
              <circle cx="25" cy="10" r="4" fill="#3b82f6"/>
              <circle cx="55" cy="10" r="4" fill="#06d881"/>
              <circle cx="85" cy="10" r="4" fill="#f59e0b"/>
            </svg>
          </div>
          <p className="premium-subtitle">
            We combine expertise, technology, and integrity to deliver unparalleled 
            investigative services that you can trust.
          </p>
        </div>

        {/* Features Grid */}
        <div className="features-premium-grid">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className={`feature-premium-card ${isVisible ? "visible" : ""}`}
              style={{ animationDelay: `${feature.delay}s` }}
            >
              <div className="card-premium-bg" style={{ background: feature.gradient }}></div>
              <div className="card-number">0{feature.id}</div>
              <div className="card-icon-wrapper" style={{ background: `${feature.color}15` }}>
                <div className="card-icon" style={{ color: feature.color }}>
                  {feature.icon}
                </div>
              </div>
              <h3 className="card-title">{feature.title}</h3>
              <p className="card-description">{feature.description}</p>
              <div className="card-stat-wrapper">
                <div className="card-stat">
                  <span className="stat-value" style={{ color: feature.color }}>{feature.stat}</span>
                  <span className="stat-label">{feature.statLabel}</span>
                </div>
              </div>
              <div className="card-shine"></div>
            </div>
          ))}
        </div>

        {/* Stats Row */}
        <div className="stats-premium-row">
          {stats.map((stat, index) => (
            <div key={index} className="stat-premium-item" style={{ animationDelay: `${0.5 + index * 0.1}s` }}>
              <div className="stat-icon-circle" style={{ background: `${stat.color}15`, color: stat.color }}>
                {stat.icon}
              </div>
              <div className="stat-info">
                <span className="stat-number">{stat.value}</span>
                <span className="stat-text">{stat.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="trust-indicators-premium">
          <div className="trust-item">
            <FaCheckCircle className="trust-check" />
            <span>ISO Certified</span>
          </div>
          <div className="trust-item">
            <FaCheckCircle className="trust-check" />
            <span>Government Licensed</span>
          </div>
          <div className="trust-item">
            <FaCheckCircle className="trust-check" />
            <span>24/7 Emergency Service</span>
          </div>
          <div className="trust-item">
            <FaCheckCircle className="trust-check" />
            <span>100% Money Back Guarantee*</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;